
import { mapGetters, mapMutations } from "vuex";
import { defineComponent, PropType } from "vue";
import Dropdown from "primevue/dropdown";
import { MarketplaceEstimatesViewModel } from "@/interfaces/MarketplaceEstimatesViewModel";
import { MedicationMarketplaceDrugListViewModel } from "@/interfaces/MedicationMarketplaceDrugListViewModel";
import swal from "sweetalert";
import { DrugPrice } from "@/interfaces/DrugPrice";
import { rxEstimateScreenView } from "@/interfaces/PharmacyCartViewEnum";

export default defineComponent({
  name: "MedicinePicker",
  components: { Dropdown },
  props: {
    medicationData: {
      type: Object as PropType<MedicationMarketplaceDrugListViewModel>,
      required: true,
    },
    isPharmacyTransfer: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedStrength: null,
      selectedType: null,
      selectedQuantity: null as { amount: string; code: string; priceData?: any } | null,
      pillQuantity: 1,
      maxPillQuantity: 180,
    };
  },
  computed: {
    ...mapGetters({
      MarketplaceEstimates: "getMarketplaceEstimates",
      SignedIn: "getSignedIn",
      Affiliate: "getAffiliate",
      Content: "getContent",
    }),
    selectedDrugName() {
      if (this.medicationData) {
        return this.medicationData.matchedTerm;
      }
      return "";
    },
    // Build unique strength options from the drug data.
    strengthOptions() {
      if (!this.medicationData || !this.medicationData.drug) return [];
      const drugs = this.medicationData.drug;
      const uniqueStrengths: { [key: string]: string } = {};
      drugs.forEach((drug: any) => {
        uniqueStrengths[drug.strength] = drug.strength;
      });
      return Object.values(uniqueStrengths).map((str: string) => ({
        amount: str,
        code: str,
      }));
    },
    // Build type options (dosageDescription) for drugs matching the selected strength.
    typeOptions() {
      if (!this.selectedStrength) return [];
      const drugs = this.medicationData.drug.filter((drug: any) => drug.strength === this.selectedStrength.code);
      const uniqueTypes: { [key: string]: string } = {};
      drugs.forEach((drug: any) => {
        uniqueTypes[drug.dosageDescription] = drug.dosageDescription;
      });
      return Object.values(uniqueTypes).map((desc: string) => ({
        type: desc,
      }));
    },
    // Choose a drug based on the selected strength and type (using the first match).
    selectedDrug() {
      if (!this.selectedStrength || !this.selectedType) return null;
      const drugs = this.medicationData.drug.filter((drug: any) => drug.strength === this.selectedStrength.code && drug.dosageDescription === this.selectedType.type);
      return drugs.length > 0 ? drugs[0] : null;
    },
    // Build quantity options from the drugPrice data that match the selected drug.
    // If the user is not signed in, restrict to only the prices for the isDefault affiliateSubscription.
    quantityOptions() {
      if (!this.selectedDrug) return [];
      let drugPrices = this.medicationData.drugPrice.filter((price: any) => price.drugID === this.selectedDrug.id);
      if (!this.SignedIn || (this.SignedIn && !this.usersCurrentAffiliateSubscription)) {
        const defaultSub = this.medicationData.affiliateSubscription.find((sub: any) => sub.isDefault);
        if (defaultSub) {
          drugPrices = drugPrices.filter((price: any) => price.affiliateSubscriptionID === defaultSub.id);
        }
      } else {
        // If user is SignedIn, check the user's subscription level and filter based off of that ID.
        if (this.medicationData.patientAffiliateSubscriptionID) {
          drugPrices = drugPrices.filter((price) => price.affiliateSubscriptionID === this.medicationData.patientAffiliateSubscriptionID);
        }
      }
      // Append a custom option.
      // drugPrices.push({ amount: "custom amount", code: "1" });

      return drugPrices.map((price: any) => ({
        amount: price.qty + " count",
        code: String(price.qty),
        priceData: price,
      }));
    },

    // Get the appropriate drugPrice based on the selected quantity.
    // Applies the default subscription filter if the user is not signed in.
    // For custom amounts (code "1"), we use the first available drugPrice as the base.

    selectedDrugPrice() {
      if (!this.selectedDrug) return null;
      let drugPrices = this.medicationData.drugPrice.filter((price: any) => price.drugID === this.selectedDrug.id);
      if (!this.SignedIn || (this.SignedIn && !this.usersCurrentAffiliateSubscription)) {
        const defaultSub = this.medicationData.affiliateSubscription.find((sub: any) => sub.isDefault);
        if (defaultSub) {
          drugPrices = drugPrices.filter((price: any) => price.affiliateSubscriptionID === defaultSub.id);
        }
      } else {
        // If user is SignedIn, check the user's subscription level and filter based off of that ID.
        if (this.medicationData.patientAffiliateSubscriptionID) {
          drugPrices = drugPrices.filter((price) => price.affiliateSubscriptionID === this.medicationData.patientAffiliateSubscriptionID);
        }
      }

      if (this.selectedQuantity) {
        if (this.selectedQuantity.code === "1") {
          return drugPrices.length > 0 ? drugPrices[0] : null;
        } else {
          return drugPrices.find((price: any) => String(price.qty) === this.selectedQuantity.code) || null;
        }
      }
      return null;
    },
    manufacturingPrice() {
      return this.selectedDrugPrice ? this.selectedDrugPrice.ingredientCost.toFixed(2) : "0.00";
    },
    markupPrice() {
      return this.selectedDrugPrice ? this.selectedDrugPrice.markup.toFixed(2) : "0.00";
    },
    laborPrice() {
      return this.selectedDrugPrice ? this.selectedDrugPrice.dispensingFee.toFixed(2) : "0.00";
    },
    // Calculate the total price (Your Price) by adding the pricing components.
    // If the user selected custom amount, multiply by pillQuantity.
    totalPrice() {
      const ingredient = this.selectedDrugPrice ? this.selectedDrugPrice.ingredientCost : 0;
      const markup = this.selectedDrugPrice ? this.selectedDrugPrice.markup : 0;
      const labor = this.selectedDrugPrice ? this.selectedDrugPrice.dispensingFee : 0;
      const shipping = this.medicationData.standardShippingFee;
      const multiplier = this.selectedQuantity && this.selectedQuantity.code === "1" ? this.pillQuantity : 1;
      const total = (ingredient + markup + labor) * multiplier + shipping;
      return total.toFixed(2);
    },

    bestSelectedDrugPrice() {
      // Make sure both a drug and a quantity have been selected.
      if (!this.selectedDrug || !this.selectedQuantity) {
        return { total: "0.00", selectedPriceObj: null };
      }

      // Filter the drug prices for the selected drug.
      let drugPrices = this.medicationData.drugPrice.filter((price) => price.drugID === this.selectedDrug.id);

      // Find the affiliate subscription with the highest tier.
      const bestSub = this.medicationData.affiliateSubscription.reduce((prev, curr) => (curr.tier > prev.tier ? curr : prev), this.medicationData.affiliateSubscription[0]);

      // Further narrow down the drugPrices to those matching the best subscription.
      drugPrices = drugPrices.filter((price) => price.affiliateSubscriptionID === bestSub.id);

      // Select the correct drugPrice based on the chosen quantity.
      // If the selected quantity's code is "1" (custom quantity), use the first available option.
      let selectedPriceObj = null;
      if (this.selectedQuantity.code === "1") {
        selectedPriceObj = drugPrices.length > 0 ? drugPrices[0] : null;
      } else {
        selectedPriceObj = drugPrices.find((price) => String(price.qty) === this.selectedQuantity.code) || null;
      }

      // If a matching price object is not found, return defaults.
      if (!selectedPriceObj) {
        return { total: "0.00", selectedPriceObj: null };
      }

      // Extract pricing components.
      const ingredient = selectedPriceObj.ingredientCost;
      const markup = selectedPriceObj.markup;
      const labor = selectedPriceObj.dispensingFee;
      const shipping = this.medicationData.standardShippingFee;

      // Determine the multiplier: use pillQuantity when using a custom amount.
      const multiplier = this.selectedQuantity && this.selectedQuantity.code === "1" ? this.pillQuantity : 1;

      // Calculate the total price.
      const total = (ingredient + markup + labor) * multiplier + shipping;

      // Return an object with both the calculated total and the full selectedPriceObj.
      return { total: total.toFixed(2), selectedPriceObj };
    },

    nextTierSelectedDrugPrice() {
      // Ensure both a drug and a quantity have been selected.
      if (!this.selectedDrug || !this.selectedQuantity) {
        return { total: "0.00", selectedPriceObj: null };
      }

      // Filter the drug prices for the selected drug.
      let drugPrices = this.medicationData.drugPrice.filter((price) => price.drugID === this.selectedDrug.id);

      // Use the next tier computed property. It should return the subscription
      // object that represents the next tier above the user's current subscription.
      const nextTier = this.nextTierUp;
      if (!nextTier) {
        return { total: "0.00", selectedPriceObj: null };
      }

      // Narrow the drugPrices list to those matching the next tier affiliate subscription.
      drugPrices = drugPrices.filter((price) => price.affiliateSubscriptionID === nextTier.id);

      // Select the correct drugPrice based on the chosen quantity.
      // For a custom quantity (code "1"), use the first available option.
      let selectedPriceObj = null;
      if (this.selectedQuantity.code === "1") {
        selectedPriceObj = drugPrices.length > 0 ? drugPrices[0] : null;
      } else {
        selectedPriceObj = drugPrices.find((price) => String(price.qty) === this.selectedQuantity.code) || null;
      }

      // If a matching price object is not found, return defaults.
      if (!selectedPriceObj) {
        return { total: "0.00", selectedPriceObj: null };
      }

      // Extract the pricing components.
      const ingredient = selectedPriceObj.ingredientCost;
      const markup = selectedPriceObj.markup;
      const labor = selectedPriceObj.dispensingFee;
      // Use the standard shipping fee from the medication data.
      const shipping = this.medicationData.standardShippingFee;

      // Determine the multiplier: if a custom quantity is selected (code "1"), multiply by pillQuantity.
      const multiplier = this.selectedQuantity && this.selectedQuantity.code === "1" ? this.pillQuantity : 1;

      // Calculate the total price.
      const total = (ingredient + markup + labor) * multiplier + shipping;

      // Return an object with both the calculated total and the full selectedPriceObj.
      return { total: total.toFixed(2), selectedPriceObj };
    },

    defaultTotalPrice() {
      // Ensure that a drug and a quantity have been selected.
      if (!this.selectedDrug || !this.selectedQuantity) return "0.00";

      // Filter the drug prices for the selected drug.
      let drugPrices = this.medicationData.drugPrice.filter((price) => price.drugID === this.selectedDrug.id);

      // Find the affiliate subscription that is marked as default.
      const defaultSub = this.medicationData.affiliateSubscription.find((sub) => sub.isDefault);

      // If found, filter the drugPrices to only those that belong to the default subscription.
      if (defaultSub) {
        drugPrices = drugPrices.filter((price) => price.affiliateSubscriptionID === defaultSub.id);
      }

      // Select the correct drugPrice based on the selected quantity.
      let selectedPriceObj = null;
      if (this.selectedQuantity.code === "1") {
        // For custom quantities, use the first available drugPrice.
        selectedPriceObj = drugPrices.length > 0 ? drugPrices[0] : null;
      } else {
        // For fixed quantities, find the matching price option.
        selectedPriceObj = drugPrices.find((price) => String(price.qty) === this.selectedQuantity.code) || null;
      }

      if (!selectedPriceObj) return "0.00";

      // Extract pricing components.
      const ingredient = selectedPriceObj.ingredientCost;
      const markup = selectedPriceObj.markup;
      const labor = selectedPriceObj.dispensingFee;
      const shipping = this.medicationData.standardShippingFee;

      // Determine the multiplier: if custom quantity (code "1") is selected, multiply by pillQuantity.
      const multiplier = this.selectedQuantity && this.selectedQuantity.code === "1" ? this.pillQuantity : 1;

      // Calculate the total price.
      const total = (ingredient + markup + labor) * multiplier + shipping;
      return total.toFixed(2);
    },

    bestAffiliateSubscription() {
      if (!this.medicationData || !this.medicationData.affiliateSubscription || this.medicationData.affiliateSubscription.length === 0) {
        return null;
      }
      return this.medicationData.affiliateSubscription.reduce((prev, curr) => {
        return curr.tier > prev.tier ? curr : prev;
      }, this.medicationData.affiliateSubscription[0]);
    },

    remainingSubscriptionsWithDrugPrices() {
      if (!this.medicationData || !this.medicationData.affiliateSubscription || this.medicationData.affiliateSubscription.length === 0) {
        return [];
      }

      const subscriptionsWithPrices = this.medicationData.affiliateSubscription
        .filter((sub) => sub.id !== (this.usersCurrentAffiliateSubscription ? this.usersCurrentAffiliateSubscription.id : null) && sub.id !== (this.bestAffiliateSubscription ? this.bestAffiliateSubscription.id : null))
        .map((sub) => {
          // Find the drug price for this subscription.
          const drugPrices = this.medicationData.drugPrice.filter((price) => price.drugID === this.selectedDrug.id && price.affiliateSubscriptionID === sub.id);

          let selectedPriceObj = null;
          if (this.selectedQuantity) {
            if (this.selectedQuantity.code === "1") {
              selectedPriceObj = drugPrices.length > 0 ? drugPrices[0] : null;
            } else {
              selectedPriceObj = drugPrices.find((price) => String(price.qty) === this.selectedQuantity.code) || null;
            }
          }

          // Calculate total price for this subscription.
          let totalPrice = "0.00";
          if (selectedPriceObj) {
            const ingredient = selectedPriceObj.ingredientCost;
            const markup = selectedPriceObj.markup;
            const labor = selectedPriceObj.dispensingFee;
            const shipping = this.medicationData.standardShippingFee;
            const multiplier = this.selectedQuantity && this.selectedQuantity.code === "1" ? this.pillQuantity : 1;
            const total = (ingredient + markup + labor) * multiplier + shipping;
            totalPrice = total.toFixed(2);
          }

          return {
            ...sub,
            drugPrices: totalPrice,
          };
        });

      return subscriptionsWithPrices;
    },

    usersCurrentAffiliateSubscription() {
      if (!this.medicationData || !this.medicationData.affiliateSubscription || !this.medicationData.patientAffiliateSubscriptionID) {
        return null;
      }

      return this.medicationData.affiliateSubscription.find((sub) => sub.id === this.medicationData.patientAffiliateSubscriptionID) || null;
    },

    nextTierUp() {
      // Make sure the current user subscription and available subscriptions exist.
      if (!this.usersCurrentAffiliateSubscription || !this.medicationData.affiliateSubscription) {
        return null;
      }

      const currentTier = this.usersCurrentAffiliateSubscription.tier;

      // Filter subscriptions that have a tier higher than the user's current subscription.
      const higherSubs = this.medicationData.affiliateSubscription.filter((sub) => sub.tier > currentTier);

      // If no subscription has a higher tier, the user is at the best level.
      if (higherSubs.length === 0) {
        return null;
      }

      // Sort the higher subscriptions in ascending order by tier.
      higherSubs.sort((a, b) => a.tier - b.tier);

      // Return the one with the smallest tier among those above the user's tier.
      return higherSubs[0];
    },

    userHasBestSubscription() {
      const best = this.bestAffiliateSubscription;
      const current = this.usersCurrentAffiliateSubscription;

      if (!best || !current) {
        return false;
      }

      return best.id === current.id;
    },
    userHasDefaultSubscription() {
      // Retrieve the current subscription using the computed property.
      const current = this.usersCurrentAffiliateSubscription;
      // If there's a valid current subscription, return its isDefault property; otherwise return false.
      return current ? current.isDefault : false;
    },
  },
  watch: {
    // When medicationData is received (or changes), set the first strength option.
    medicationData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Use $nextTick to ensure that the computed property has been updated.
          this.$nextTick(() => {
            if (this.strengthOptions.length > 0) {
              this.selectedStrength = this.strengthOptions[0];
            }
          });
        }
      },
    },
    // When a strength is selected, auto-select the first available type.
    selectedStrength(newVal) {
      // Using $nextTick ensures that typeOptions has updated (since it depends on selectedStrength)
      this.$nextTick(() => {
        if (this.typeOptions.length > 0) {
          this.selectedType = this.typeOptions[0];
        } else {
          this.selectedType = null;
        }
      });
      // Clear any previously selected quantity.
      this.selectedQuantity = null;
    },
    // When a type is selected, auto-select the first available quantity.
    selectedType(newVal) {
      this.$nextTick(() => {
        if (this.quantityOptions.length > 0) {
          this.selectedQuantity = this.quantityOptions[0];
        } else {
          this.selectedQuantity = null;
        }
      });
    },
  },
  methods: {
    ...mapMutations({
      setMarketplaceEstimates: "setMarketplaceEstimates",
      setShowPharmacyCart: "setShowPharmacyCart",
    }),
    decreaseQuantity() {
      if (this.pillQuantity > 1) {
        this.pillQuantity--;
      }
    },
    increaseQuantity() {
      if (this.pillQuantity < this.maxPillQuantity) {
        this.pillQuantity++;
      }
    },
    async addEstimate() {
      // Make sure we have a valid selection.
      if (!this.selectedDrug || !this.selectedQuantity) {
        console.warn("Incomplete selection: cannot add estimate.");
        return;
      }

      // Determine the quantity:
      // If the user selected the custom amount option (code "1"), use pillQuantity.
      // Otherwise, use the quantity provided by the selected option.
      const qty = this.selectedQuantity.code === "1" ? this.pillQuantity : parseInt(this.selectedQuantity.code, 10);

      // Build the new estimate object matching the MarketplaceEstimates interface.
      const newEstimate: MarketplaceEstimatesViewModel = {
        iD: null, // new record; id will be assigned later
        drugID: this.selectedDrug.id,
        affiliateID: null,
        userID: null,
        drugName: this.medicationData.matchedTerm,
        qty: qty,
        dose: this.selectedStrength.amount,
        drugType: this.selectedType.type,
        estimatedPrice: this.totalPrice,
        createdDate: null,
        modifiedDate: new Date().toISOString(),
        active: true,
      };

      // Get the current estimates array
      let currentEstimates = Array.isArray(this.MarketplaceEstimates) ? [...this.MarketplaceEstimates] : [];

      // Find existing estimate with the same drugID and qty
      const existingEstimateIndex = currentEstimates.findIndex((estimate) => estimate.drugID === newEstimate.drugID && estimate.qty === newEstimate.qty);

      if (existingEstimateIndex !== -1) {
        // Update the modifiedDate of the existing estimate and move it to the front
        const updatedEstimate = {
          ...currentEstimates[existingEstimateIndex],
          modifiedDate: new Date().toISOString(),
        };

        // Remove the existing estimate from its current position
        currentEstimates.splice(existingEstimateIndex, 1);

        // Insert the updated estimate at the front
        currentEstimates.unshift(updatedEstimate);
      } else {
        // Add the new estimate at the front
        currentEstimates.unshift(newEstimate);
      }

      // Commit the new array back to the Vuex store.
      this.setMarketplaceEstimates(currentEstimates);

      if (!this.isPharmacyTransfer) {
        this.setShowPharmacyCart({ show: true, type: "rxEstimate", view: rxEstimateScreenView.cart });
      }
      if (this.isPharmacyTransfer) {
        const payload = Array.isArray(newEstimate) ? newEstimate : [newEstimate];
        this.$emit("closeSlide", payload);
      }
      this.$bvModal.hide("modal-mp");
      this.$emit("hideRXChoice");

      // if user is signed in, send it to the server to be added to the MarketplaceEstimates table
      if (this.SignedIn) {
        // Convert single object to an array to utilize the dynamic endpoint for updating marketplace estimates. This is an edge case solution for updating a single marketplace estimate.
        // It needs to an accept an array for when a user signs in or signs up to add all of their existing estimates that are in the store.
        const payload = Array.isArray(newEstimate) ? newEstimate : [newEstimate];

        await this.$store.dispatch("UpdateMarketplaceEstimates", payload);
      }
    },
  },
});
